'use strict';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
} from '@mui/material';
import { useSearchProjectsQuery } from '@/redux/services/portal/admin.api';
import { SearchBox } from '@/components/index.jsx';
import './AddScopeModal.scss';

// Stop walking automatically after this many full pages so a very broad query
// (every code starting with "M") cannot request the whole catalog on its own.
export const ADD_PROJECT_SEARCH_AUTO_PAGE_CAP = 25;

const pageIndexForSkip = (skip, pageSize) => Math.floor(skip / pageSize);

export const unassignedProjects = (pages, getProjectId, excludedIds) => {
	const seen = new Set();
	const visible = [];
	pages.flat().forEach((item) => {
		const projectId = getProjectId(item);
		if (!projectId || excludedIds.has(projectId) || seen.has(projectId)) return;
		seen.add(projectId);
		visible.push(item);
	});
	return visible;
};

const AddProjectToUserModal = ({
	open,
	onCancel,
	onConfirm,
	pageSize,
	user,
	getProjectId,
	getProjectCode,
	getProjectTitle,
	existingProjectIds,
	isSubmitting,
	statusMessage,
}) => {
	const [searchText, setSearchText] = useState('');
	const [selectedProjects, setSelectedProjects] = useState(new Map());
	const [requestSkip, setRequestSkip] = useState(0);
	const [pages, setPages] = useState([]);

	const resetSearch = useCallback((value) => {
		setSearchText(value);
		setRequestSkip(0);
		setPages([]);
	}, []);

	useEffect(() => {
		if (open) {
			resetSearch('');
			setSelectedProjects(new Map());
		}
	}, [open, resetSearch]);

	const shouldSearch = open && searchText.trim().length >= 1;
	const searchQuery = shouldSearch ? searchText.toUpperCase() : '';
	const searchResult = useSearchProjectsQuery(
		{ searchText: searchQuery, skip: requestSkip, take: pageSize, isolatedPage: true },
		{ skip: !shouldSearch }
	);

	useEffect(() => {
		if (!shouldSearch || !searchResult.isSuccess || searchResult.isFetching) return;
		if (searchResult.originalArgs?.searchText !== searchQuery) return;
		if (searchResult.originalArgs?.skip !== requestSkip) return;
		const page = Array.isArray(searchResult.data) ? searchResult.data : [];
		setPages((current) => {
			const index = pageIndexForSkip(requestSkip, pageSize);
			const stored = current[index];
			const samePage = stored === page
				|| (Array.isArray(stored)
					&& stored.length === page.length
					&& stored.every((item, itemIndex) => item === page[itemIndex]));
			if (samePage) return current;
			const next = current.slice();
			next[index] = page;
			return next;
		});
	}, [shouldSearch, searchResult.isSuccess, searchResult.isFetching, searchResult.data, searchResult.originalArgs, searchQuery, requestSkip, pageSize]);

	const excludedIds = useMemo(() => new Set([
		...existingProjectIds,
		...Array.from(selectedProjects.keys()),
	]), [existingProjectIds, selectedProjects]);

	const loadedPages = useMemo(
		() => pages.filter((page) => Array.isArray(page)),
		[pages]
	);
	const lastPage = loadedPages.length > 0 ? loadedPages[loadedPages.length - 1] : null;
	const hasMore = Array.isArray(lastPage) && lastPage.length >= pageSize;
	const reachedAutoCap = loadedPages.length >= ADD_PROJECT_SEARCH_AUTO_PAGE_CAP;

	const results = useMemo(() => {
		return unassignedProjects(loadedPages, getProjectId, excludedIds).map((item) => ({
			...item,
			projectId: getProjectId(item),
			projectCode: getProjectCode(item),
			projectTitle: getProjectTitle(item),
		}));
	}, [loadedPages, getProjectId, getProjectCode, getProjectTitle, excludedIds]);
	// A failed next page must not look like a walk still in progress: the effect
	// already stops on isError, so leaving this true hides the list behind a spinner.
	const walkingForUnassigned = shouldSearch
		&& !searchResult.isError
		&& results.length === 0
		&& hasMore
		&& !reachedAutoCap;

	// A full page of projects the user already has leaves an empty list and no scrollbar.
	// Keep requesting the next page until an unassigned project shows up, or the results end.
	useEffect(() => {
		if (!shouldSearch || searchResult.isFetching || searchResult.isError) return;
		if (results.length > 0 || !hasMore || reachedAutoCap) return;
		if (!loadedPages[pageIndexForSkip(requestSkip, pageSize)]) return;
		setRequestSkip(loadedPages.length * pageSize);
	}, [shouldSearch, searchResult.isFetching, searchResult.isError, results.length, hasMore, reachedAutoCap, loadedPages, requestSkip, pageSize]);
	const selectedItems = useMemo(() => Array.from(selectedProjects.values()), [selectedProjects]);

	const handleToggleProject = (project) => {
		const projectId = getProjectId(project);
		if (!projectId) return;
		setSelectedProjects((prev) => {
			const next = new Map(prev);
			if (next.has(projectId)) {
				next.delete(projectId);
			} else {
				next.set(projectId, project);
			}
			return next;
		});
	};

	const handleConfirm = () => {
		if (selectedProjects.size === 0 || isSubmitting) return;
		onConfirm(Array.from(selectedProjects.values()));
	};

	return (
		<Box className="AddScopeModal" sx={{ mt: 1 }}>
			<SearchBox
				iconColor="tertiary"
				showResults={false}
				hintText="Search projects"
				autoComplete="off"
				name="add-project-search"
				disabled={!user || isSubmitting}
				onChange={resetSearch}
				handleCancel={() => resetSearch('')}
				mapData={{ primaryText: 'name', value: 'id' }}
				dataSource={[]}
			/>
			{!user ? (
				<Typography variant="body2" sx={{ mt: 2 }}>
					Select a user before adding a project.
				</Typography>
			) : null}
			{statusMessage ? (
				<Typography variant="body2" color="error" sx={{ mt: 2 }} data-testid="add-project-status">
					{statusMessage}
				</Typography>
			) : null}
			{user && !shouldSearch ? (
				<Typography variant="body2" sx={{ mt: 2 }}>
					Start typing to search for a project.
				</Typography>
			) : null}
			{(searchResult.isFetching || walkingForUnassigned) && results.length === 0 ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
					<CircularProgress size={20} />
				</Box>
			) : null}
			{selectedItems.length > 0 ? (
				<>
					<Typography variant="subtitle2" sx={{ mt: 2 }}>
						Selected Projects
					</Typography>
					<List dense className="AddScopeModalList">
						{selectedItems.map((project) => (
							<ListItem
								button
								key={getProjectId(project)}
								selected
							onClick={() => {
								if (!isSubmitting) {
									handleToggleProject(project);
								}
							}}
							>
								<ListItemIcon>
									<Checkbox
										edge="start"
										checked
										tabIndex={-1}
										disableRipple
									disabled={isSubmitting}
									/>
								</ListItemIcon>
								<ListItemText
									primary={getProjectCode(project) || getProjectId(project)}
									secondary={getProjectTitle(project)}
								/>
							</ListItem>
						))}
					</List>
				</>
			) : null}
			{shouldSearch && !walkingForUnassigned && (results.length > 0 || !searchResult.isFetching) ? (
				<List dense className="AddScopeModalList">
					{results.map((project) => (
						<ListItem
							button
							key={getProjectId(project)}
							selected={selectedProjects.has(getProjectId(project))}
							onClick={() => {
								if (!isSubmitting) {
									handleToggleProject(project);
								}
							}}
						>
							<ListItemIcon>
								<Checkbox
									edge="start"
									checked={selectedProjects.has(getProjectId(project))}
									tabIndex={-1}
									disableRipple
									disabled={isSubmitting}
								/>
							</ListItemIcon>
							<ListItemText
								primary={getProjectCode(project) || getProjectId(project)}
								secondary={getProjectTitle(project)}
							/>
						</ListItem>
					))}
					{results.length === 0 && searchResult.isError ? (
						<ListItem data-testid="add-project-search-error">
							<ListItemText primary="Could not load projects. Try the search again." />
							<Button onClick={() => searchResult.refetch()} disabled={isSubmitting}>
								Retry
							</Button>
						</ListItem>
					) : null}
					{results.length === 0 && !searchResult.isError ? (
						<ListItem>
							<ListItemText
								primary={hasMore && reachedAutoCap
									? `No unassigned projects in the first ${loadedPages.length * pageSize} matches.`
									: 'No projects found'}
							/>
						</ListItem>
					) : null}
				</List>
			) : null}
			{shouldSearch && hasMore && (results.length > 0 || reachedAutoCap) ? (
				<Box sx={{ textAlign: 'center', mt: 1 }}>
					<Button
						onClick={() => setRequestSkip(loadedPages.length * pageSize)}
						disabled={searchResult.isFetching || isSubmitting}
						data-testid="add-project-show-more"
					>
						{searchResult.isFetching ? 'Loading...' : 'Show more'}
					</Button>
				</Box>
			) : null}
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
				<Button onClick={onCancel} sx={{ mr: 1 }} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleConfirm}
					disabled={selectedProjects.size === 0 || isSubmitting}
				>
					{isSubmitting ? 'Saving...' : `Add Selected (${selectedProjects.size})`}
				</Button>
			</Box>
		</Box>
	);
};

AddProjectToUserModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	user: PropTypes.object,
	getProjectId: PropTypes.func.isRequired,
	getProjectCode: PropTypes.func.isRequired,
	getProjectTitle: PropTypes.func.isRequired,
	existingProjectIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
	isSubmitting: PropTypes.bool,
	statusMessage: PropTypes.string,
};

AddProjectToUserModal.defaultProps = {
	user: null,
	existingProjectIds: [],
	isSubmitting: false,
	statusMessage: '',
};

export default AddProjectToUserModal;
